import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';

const ChipGuidePage = () => {
  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="mystical-card">
            <h1 className="mystical-title text-black mb-8">
              How to Count Chips
            </h1>

            {/* What are Chips */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                What are Chips?
              </h2>
              <div className="space-y-4">
                <p className="mystical-text text-black leading-relaxed">
                  Chips are a tally system that you'll use in the ARPG to track your progress throughout the game! Chips are never spent or expire. They exist to act as an account-wide benchmark for all of your Veilkin's progress and help you qualify for new upgrades.
                </p>
                <p className="mystical-text text-black leading-relaxed">
                  This is a guide on how to count Chips!
                </p>
              </div>
            </div>

            {/* What Counts for Chips */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                What Counts for Chips?
              </h2>
              <div className="space-y-4">
                <p className="mystical-text text-black leading-relaxed">
                  Any submission to Mystveil Township counts towards Chips, as long as it meets the blanket Chip requirements. This means that as long as it's made at least partially by you, we can tell at a visual glance what type of art you are going for and it's purely Mystveil Township related.
                </p>
                <p className="mystical-text text-black leading-relaxed">
                  Please note that only the person who posts and submits the Mystveil submission first can count for Chips, regardless of any collaboration.
                </p>
              </div>
            </div>

            {/* Bust or More */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Bust or More
              </h2>
              <p className="mystical-text text-black leading-relaxed">
                Anything above a bust counts for Chips. Headshots or "floating head" images do not count for Chips.
              </p>
            </div>

            {/* Two Thirds Rule */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Two Thirds Rule
              </h2>
              <div className="w-full mb-6 rounded-xl overflow-hidden shadow-lg mystical-glow">
                <img 
                  src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/2%203rds%20rule.png" 
                  alt="Two thirds rule example" 
                  className="w-full h-auto block object-cover"
                />
              </div>
              <p className="mystical-text text-black leading-relaxed">
                If the character's head is not visible, at least two thirds of the character must be visually seen in order to count for Chips.
              </p>
            </div>

            {/* Multiple Poses */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Multiple Poses
              </h2>
              <p className="mystical-text text-black leading-relaxed">
                Each pose must be individually redrawn in order to count for Chips.
              </p>
            </div>

            {/* Writing vs Art */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Writing vs Art
              </h2>
              <div className="w-full mb-6 rounded-xl overflow-hidden shadow-lg mystical-glow">
                <img 
                  src="/images/writing.png" 
                  alt="Writing example" 
                  className="w-full h-auto block object-cover"
                />
              </div>
              <p className="mystical-text text-black mb-6 leading-relaxed">
                Within Mystveil Township, we believe writing is a form of art as well! We have a comparison system where the amount of words = the type of art you are going for. Our comparison is the following:
              </p>
              <div className="mystical-card bg-purple-500 bg-opacity-10 mb-6">
                <ul className="space-y-2">
                  <li className="mystical-text text-gray-800">500 Words = Sketch/Lined</li>
                  <li className="mystical-text text-gray-800">1500 Words = Flat Color</li>
                  <li className="mystical-text text-gray-800">2500 Words = Polished</li>
                  <li className="mystical-text text-gray-800">Every additional 500 words = +1 Bonus Chip</li>
                </ul>
              </div>
              <p className="mystical-text text-black leading-relaxed">
                If your word count does not get close to the exact amount, round it to the closest! Within the Chip tracker, if your Mystiveil entry has both writing and a story, they count as separate entries in the Chip Tracker.
              </p>
            </div>

            {/* Chip Submission Rules */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Chip Submission Rules
              </h2>
              
              <h3 className="mystical-subtitle text-black text-xl mb-4">Characters</h3>
              <div className="mystical-card bg-purple-500 bg-opacity-10 mb-6">
                <ul className="space-y-2">
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Maximum Character Count:</span> A maximum of 20 characters can be counted per uploaded piece. Once submitted, the image cannot be edited or changed.
                  </li>
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Official Velkin:</span> All official Velkin must have an ID number to count for Chips.
                  </li>
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Unofficial NPCs:</span> Can be included alongside official Velkin but must be used sparingly, cannot be in event images, and don't count for Chips.
                  </li>
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Design Requirements:</span> Official Velkin's designs must be current within one month of the last applied upgrade.
                  </li>
                </ul>
              </div>

              <h3 className="mystical-subtitle text-black text-xl mb-4">Official Fauna and Pets</h3>
              <div className="mystical-card bg-purple-500 bg-opacity-10 mb-6">
                <ul className="space-y-2">
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Fauna:</span> Any listed fauna can be drawn for Chips, counting as one character each.
                  </li>
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Pets:</span> Must have an ID number to count for Chips. Unofficial pets don't count.
                  </li>
                </ul>
              </div>

              <h3 className="mystical-subtitle text-black text-xl mb-4">Official Flora</h3>
              <div className="mystical-card bg-purple-500 bg-opacity-10 mb-6">
                <ul className="space-y-2">
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Flora Listing:</span> Any listed flora counts for Chips, but only one instance per image.
                  </li>
                  <li className="mystical-text text-gray-800">
                    <span className="font-bold text-purple-600">Multiple Patches:</span> Multiple patches of the same flora count as one character; different flora types count separately.
                  </li>
                </ul>
              </div>
            </div>

            {/* Simple Background */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Simple Background: +2 Chips
              </h2>
              <p className="mystical-text text-black leading-relaxed">
                Draw a Veilkin interacting with a background and midground. Must not be an abstract background or a single color, and show an environment behind/around the Veilkin. Can be shaded or not, but must be colored.
              </p>
            </div>

            {/* Visual Guide for Chip Counting */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Visual Guide for Chip Counting
              </h2>
              <div className="space-y-6">
                <div className="mystical-card bg-purple-500 bg-opacity-10">
                  <ul className="space-y-4">
                    <li className="mystical-text text-gray-800">Sketch/Lineart: 1 Chip</li>
                    <div className="w-full mb-4 rounded-xl overflow-hidden shadow-lg mystical-glow">
                      <img 
                        src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20sketch.png" 
                        alt="Sketch Example" 
                        className="w-full h-auto block object-cover"
                      />
                    </div>
                    
                    <li className="mystical-text text-gray-800">Flat Color/Simple Shading: 2 Chips</li>
                    <div className="w-full mb-4 rounded-xl overflow-hidden shadow-lg mystical-glow">
                      <img 
                        src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20simple%20shading.png" 
                        alt="Shading Example" 
                        className="w-full h-auto block object-cover"
                      />
                    </div>

                    <li className="mystical-text text-gray-800">Complex/Polished Piece: 4 Chips</li>
                    <div className="w-full mb-4 rounded-xl overflow-hidden shadow-lg mystical-glow">
                      <img 
                        src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20polished%20shading.png" 
                        alt="Polished Example" 
                        className="w-full h-auto block object-cover"
                      />
                    </div>

                    <li className="mystical-text text-gray-800">Simple Background: +2 Chips</li>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="rounded-xl overflow-hidden shadow-lg mystical-glow">
                        <img 
                          src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20simple%20bg%20no.png" 
                          alt="Simple Background Not Acceptable" 
                          className="w-full h-auto block object-cover"
                        />
                      </div>
                      <div className="rounded-xl overflow-hidden shadow-lg mystical-glow">
                        <img 
                          src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20simple%20bg%20pattern%20no.png" 
                          alt="Simple Background Pattern Not Acceptable" 
                          className="w-full h-auto block object-cover"
                        />
                      </div>
                      <div className="rounded-xl overflow-hidden shadow-lg mystical-glow">
                        <img 
                          src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20simple%20bg%20yes.png" 
                          alt="Simple Background Acceptable" 
                          className="w-full h-auto block object-cover"
                        />
                      </div>
                      <div className="rounded-xl overflow-hidden shadow-lg mystical-glow">
                        <img 
                          src="https://cdn.4fingerstudios.com/Mystveil/guide%20images/Example%20render%20simple%20bg%20pattern%20yes.png" 
                          alt="Simple Background Pattern Acceptable" 
                          className="w-full h-auto block object-cover"
                        />
                      </div>
                    </div>

                    <li className="mystical-text text-gray-800">Complex Background: +4 Chips</li>
                    <p className="mystical-text text-gray-800 mb-4">
                      Draw a Veilkin interacting with a background, midground and foreground. Must have 3 or more detail elements in a background, which must be defined and clearly seen, each element must be unique. Must have complex/detailed shading on the background.
                    </p>

                    <h3 className="mystical-subtitle text-gray-800 text-lg mb-2">Detail Background Element Examples</h3>
                    <ul className="space-y-1 mb-4">
                      <li className="mystical-text text-gray-800">Props: furniture, decorations, official flora</li>
                      <li className="mystical-text text-gray-800">Architecture: houses, structures, fountains</li>
                      <li className="mystical-text text-gray-800">Texture Elements: stones, bushes, branches to a tree</li>
                    </ul>

                    <li className="mystical-text text-gray-800">Bonuses</li>
                    <ul className="space-y-1 mb-4">
                      <li className="mystical-text text-gray-800">Crafts: 4 Chips</li>
                      <li className="mystical-text text-gray-800">Animations: 4 Chips</li>
                      <li className="mystical-text text-gray-800">3D Models: 3 Chips</li>
                      <li className="mystical-text text-gray-800">Holy Shit: 5 Chips</li>
                    </ul>
                    <p className="mystical-text text-gray-800">
                      Please note that these bonuses can stack, just make sure to label them in your Chip tracker notes!
                    </p>
                  </ul>
                </div>
              </div>
            </div>

            {/* Special Bonus */}
            <div className="mystical-card mb-8">
              <p className="mystical-text text-black leading-relaxed">
                Sometimes a submission goes above and beyond all requirements, often having extra elements that are otherwise not compensated by the Chip tracker. If a Dev team member feels that a piece of art deserves this special bonus, they will tag you in moderation. Only Dev team members can give this bonus.
              </p>
            </div>

            {/* How to Set Up Chip Tracker */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                How to Set Up Your Chip Tracker
              </h2>
              <div className="mystical-card bg-purple-500 bg-opacity-10">
                <ol className="space-y-2">
                  <li className="mystical-text text-gray-800">
                    Open the <a href="https://docs.google.com/spreadsheets/d/1y-MlGmJXmAzbxZRhELiuCB9xeJogihjSeoLWhhMc66E/edit?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline">Mystveil Chips Tracker</a>.
                  </li>
                  <li className="mystical-text text-gray-800">
                    Go to <strong>File > Make Copy</strong> and name it "[Your Username] Chips Tracker."
                  </li>
                  <li className="mystical-text text-gray-800">
                    Add your Toyhouse and Discord usernames.
                  </li>
                  <li className="mystical-text text-gray-800">
                    Link your art piece, select the type of art, number of characters, and background type. The sheet calculates your total!
                  </li>
                </ol>
              </div>
            </div>
            
            {/* Need Help */}
            <div className="mystical-card mb-8">
              <h2 className="mystical-subtitle text-black text-2xl mb-6">
                Need Help?
              </h2>
              <p className="mystical-text text-black leading-relaxed">
                Contact our team on <a href="https://toyhou.se/~world/199635.mystveil-township" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline">Toyhouse</a> or <a href="https://discord.gg/98Ae5exY6m" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline">Discord</a> if you have questions!
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/guide" className="mystical-button">
                Back to Guides
              </Link>
              <a 
                href="https://docs.google.com/spreadsheets/d/1y-MlGmJXmAzbxZRhELiuCB9xeJogihjSeoLWhhMc66E/edit?usp=sharing" 
                className="mystical-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Chip Tracker
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ChipGuidePage;